/**
 * PhotoGallery — reusable multi-file image uploader for wizard steps.
 *
 * Features
 * ────────
 *  • Multi-file upload against `POST /api/uploads/image` (Emergent Object Storage).
 *  • Client-side MIME + size pre-flight so obvious errors don't touch the server.
 *  • Per-file progress via axios `onUploadProgress`.
 *  • Live preview grid, drag-and-drop to reorder (HTML5 native), the first
 *    position doubles as "cover" — the widget/BookingEngine already reads
 *    `photos[0]` as the hero image.
 *  • Star toggle promotes any photo to cover.
 *  • Trash to remove a single photo (does NOT delete from object storage
 *    to avoid orphaning shared URLs — a nightly cleanup job handles that).
 *  • URL paste fallback stays available (user rule §wizard-photos §5).
 *
 * Storage contract (backwards compatible)
 * ───────────────────────────────────────
 * Parent owns `value: string[]` (an ordered array of URLs). `onChange(next)`
 * receives the whole array. Index 0 is treated as the cover throughout the
 * app — do NOT introduce a parallel `cover_url` field.
 */
import { useRef, useState } from "react";
import { Upload, Star, Trash2, GripVertical, Link2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const MAX_BYTES = 5 * 1024 * 1024;
const ALLOWED = ["image/png", "image/jpeg", "image/webp", "image/gif", "image/svg+xml"];

function absUrl(u) {
    if (!u) return u;
    if (u.startsWith("http")) return u;
    return `${process.env.REACT_APP_BACKEND_URL || ""}${u}`;
}

export default function PhotoGallery({
    value = [],
    onChange,
    kind = "hotel-photo",
    testId = "photo-gallery",
    maxCount = 20,
    allowUrl = true,
}) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState([]); // [{id, name, progress}]
    const [urlInput, setUrlInput] = useState("");
    const [dragIdx, setDragIdx] = useState(null);

    const list = Array.isArray(value) ? value : [];

    // ── Upload ──────────────────────────────────────────────────────────
    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const room = maxCount - list.length;
        if (room <= 0) {
            toast.error(`Maksimumi ${maxCount} foto. Fshi një më parë.`);
            return;
        }
        const accepted = files.slice(0, room);
        // Client-side pre-flight
        const bad = accepted.find((f) => !ALLOWED.includes(f.type));
        if (bad) {
            toast.error(`Format i pambështetur: ${bad.type || "?"}. Lejohen: PNG, JPG, WebP, GIF, SVG.`);
            return;
        }
        const tooBig = accepted.find((f) => f.size > MAX_BYTES);
        if (tooBig) {
            toast.error(`${tooBig.name} është ${(tooBig.size / 1024 / 1024).toFixed(1)} MB. Maksimumi 5 MB.`);
            return;
        }
        // Kick off in parallel; append URLs as each finishes.
        const tickets = accepted.map((f) => ({
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: f.name, progress: 0,
        }));
        setUploading((s) => [...s, ...tickets]);

        const newUrls = [];
        await Promise.all(accepted.map(async (file, i) => {
            const ticket = tickets[i];
            try {
                const fd = new FormData();
                fd.append("file", file);
                // NOTE: DO NOT set Content-Type manually. Axios + browser add
                // `multipart/form-data; boundary=…` for us; overriding without
                // a boundary breaks the multipart parser on FastAPI's side.
                const { data } = await api.post(`/uploads/image?kind=${encodeURIComponent(kind)}`, fd, {
                    onUploadProgress: (e) => {
                        if (!e.total) return;
                        const pct = Math.round((e.loaded / e.total) * 100);
                        setUploading((s) => s.map((t) => t.id === ticket.id ? { ...t, progress: pct } : t));
                    },
                });
                if (data?.url) newUrls.push(data.url);
            } catch (err) {
                const d = err?.response?.data?.detail;
                const msg = typeof d === "object" && d?.message ? d.message : (typeof d === "string" ? d : "Ngarkimi dështoi");
                toast.error(`${ticket.name}: ${msg}`);
            } finally {
                setUploading((s) => s.filter((t) => t.id !== ticket.id));
            }
        }));
        if (newUrls.length) onChange([...list, ...newUrls]);
    };

    const addUrl = () => {
        const u = (urlInput || "").trim();
        if (!u) return;
        onChange([...list, u]);
        setUrlInput("");
    };

    const removeAt = (idx) => {
        const next = list.slice();
        next.splice(idx, 1);
        onChange(next);
    };

    const promoteToCover = (idx) => {
        if (idx === 0) return;
        const next = list.slice();
        const [item] = next.splice(idx, 1);
        next.unshift(item);
        onChange(next);
    };

    // ── Drag-and-drop reordering (native HTML5) ─────────────────────────
    const onDragStart = (idx) => (e) => {
        setDragIdx(idx);
        e.dataTransfer.effectAllowed = "move";
    };
    const onDragOver = (idx) => (e) => {
        e.preventDefault();
        if (dragIdx === null || dragIdx === idx) return;
    };
    const onDrop = (idx) => (e) => {
        e.preventDefault();
        if (dragIdx === null || dragIdx === idx) return;
        const next = list.slice();
        const [moved] = next.splice(dragIdx, 1);
        next.splice(idx, 0, moved);
        onChange(next);
        setDragIdx(null);
    };
    const onDragEnd = () => setDragIdx(null);

    return (
        <div className="space-y-2" data-testid={testId}>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="akm-btn-ghost text-[12px]"
                    data-testid={`${testId}-add-button`}
                    disabled={list.length >= maxCount}
                >
                    <Upload size={13} /> Ngarko foto ({list.length}/{maxCount})
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept={ALLOWED.join(",")}
                    multiple
                    className="hidden"
                    onChange={(e) => { handleFiles(e.target.files); if (fileRef.current) fileRef.current.value = ""; }}
                />
                {allowUrl && (
                    <div className="flex flex-1 items-center gap-1.5 min-w-[220px]">
                        <input
                            className="akm-input flex-1"
                            placeholder="https://…/photo.jpg (opsional)"
                            value={urlInput}
                            onChange={(e) => setUrlInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addUrl(); } }}
                            data-testid={`${testId}-url-input`}
                        />
                        <button type="button" onClick={addUrl} className="akm-btn-ghost text-[12px]"
                                data-testid={`${testId}-url-add`}>
                            <Link2 size={12} /> Shto URL
                        </button>
                    </div>
                )}
            </div>

            {uploading.map((t) => (
                <div key={t.id} className="rounded-md border border-border bg-muted/30 px-2 py-1.5" data-testid={`${testId}-uploading-${t.id}`}>
                    <div className="flex items-center justify-between text-[11px]">
                        <span className="truncate">{t.name}</span>
                        <span className="text-muted-foreground">{t.progress}%</span>
                    </div>
                    <div className="mt-1 h-1 rounded bg-muted overflow-hidden">
                        <div className="h-1 bg-primary transition-[width]" style={{ width: `${t.progress}%` }} />
                    </div>
                </div>
            ))}

            {list.length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                    {list.map((url, idx) => (
                        <div
                            key={`${url}-${idx}`}
                            draggable
                            onDragStart={onDragStart(idx)}
                            onDragOver={onDragOver(idx)}
                            onDrop={onDrop(idx)}
                            onDragEnd={onDragEnd}
                            className={`group relative overflow-hidden rounded-md border ${idx === 0 ? "border-primary" : "border-border"} bg-card`}
                            data-testid={`${testId}-item-${idx}`}
                        >
                            <img src={absUrl(url)} alt=""
                                 className="h-28 w-full object-cover"
                                 onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
                            <div className="absolute inset-x-0 top-0 flex items-start justify-between p-1">
                                {idx === 0 ? (
                                    <span className="rounded-full bg-primary/90 px-1.5 py-0.5 text-[9px] font-medium text-primary-foreground">
                                        <Star size={9} className="inline mb-0.5" /> Kopertina
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={() => promoteToCover(idx)}
                                        className="rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                                        title="Bëje kopertinë"
                                        data-testid={`${testId}-cover-${idx}`}
                                    >
                                        <Star size={11} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => removeAt(idx)}
                                    className="rounded-full bg-black/50 p-1 text-white opacity-0 transition-opacity group-hover:opacity-100"
                                    title="Fshi"
                                    data-testid={`${testId}-remove-${idx}`}
                                >
                                    <Trash2 size={11} />
                                </button>
                            </div>
                            <div className="absolute bottom-1 left-1 rounded bg-black/50 p-0.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
                                <GripVertical size={10} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {list.length === 0 && uploading.length === 0 && (
                <div className="rounded-md border border-dashed border-border bg-muted/20 py-4 text-center text-[12px] text-muted-foreground">
                    Ende s'ka foto. Ngarko nga PC ose ngjit një URL më lart.
                </div>
            )}
        </div>
    );
}
